
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { 
    Bold, Italic, Strikethrough, Link as LinkIcon, Code, 
    VenetianMask, Smile, Type, Undo, Redo, X, Check
} from 'lucide-react';
import { EmojiPicker } from './EmojiPicker';
import { anyInputToMarkdown, markdownToEditorHtml, htmlToMarkdown } from '../../utils/markdownConverter';
import { insertHtmlAtCursor } from '../../utils/htmlUtils';

interface RichMarkdownEditorProps {
    value: string; // Markdown string from parent
    onChange: (markdown: string) => void;
    placeholder?: string;
    supportsFormatting?: boolean;
    readOnly?: boolean;
}

export const RichMarkdownEditor: React.FC<RichMarkdownEditorProps> = ({ 
    value, 
    onChange, 
    placeholder = "Введите текст...",
    supportsFormatting = true,
    readOnly = false
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const [showEmoji, setShowEmoji] = useState(false);
    const isFocused = useRef(false);
    const isComposing = useRef(false); // IME composition (Chinese/JP/Korean keyboards)
    const lastSetHtml = useRef<string>(''); // Track what we last set so we don't loop

    // Link Modal State
    const savedSelection = useRef<Range | null>(null);
    const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkText, setLinkText] = useState('');

    const saveSelection = () => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
            savedSelection.current = sel.getRangeAt(0).cloneRange();
        }
    };

    const restoreSelection = () => {
        const sel = window.getSelection();
        if (sel && savedSelection.current) {
            sel.removeAllRanges();
            sel.addRange(savedSelection.current);
        }
    };

    // ─── Initialization & External Sync ────────────────────────────────────────

    useEffect(() => {
        if (!editorRef.current) return;
        const html = markdownToEditorHtml(value);
        // Avoid resetting cursor when the user is actively editing
        if (!isFocused.current && html !== lastSetHtml.current) {
            editorRef.current.innerHTML = html;
            lastSetHtml.current = html;
        } else if (!isFocused.current) {
            // Form was cleared (value became empty)
            editorRef.current.innerHTML = html;
            lastSetHtml.current = html;
        }
    }, [value]);

    // ─── Input Handler ─────────────────────────────────────────────────────────

    const handleInput = useCallback(() => {
        if (!editorRef.current || readOnly || isComposing.current) return;
        const html = editorRef.current.innerHTML;
        const markdown = htmlToMarkdown(html);
        onChange(markdown);
    }, [onChange, readOnly]);

    // ─── Keyboard Handling ─────────────────────────────────────────────────────

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (readOnly) return;

        // Shift+Enter → soft line break (\n), which contentEditable would normally 
        // render as a <br>. We want to INSERT a newline character directly so the 
        // markdown serialization produces \n (not \n\n from a new block).
        // We DON'T override plain Enter — let the browser create its block element,
        // and our htmlToMarkdown will collapse <p></p> to \n\n.
        if (e.key === 'Enter' && e.shiftKey) {
            e.preventDefault();
            document.execCommand('insertHTML', false, '<br>');
            setTimeout(handleInput, 0);
        }
    }, [readOnly, handleInput]);

    // ─── Paste Handler ─────────────────────────────────────────────────────────

    const handlePaste = useCallback((e: React.ClipboardEvent) => {
        if (readOnly) return;
        e.preventDefault();

        const html = e.clipboardData.getData('text/html');
        const text = e.clipboardData.getData('text/plain');

        // Use the universal normalizer — works for any input source
        const raw = html || text;
        if (!raw) return;

        const markdown = anyInputToMarkdown(raw);
        const displayHtml = markdownToEditorHtml(markdown);

        // Insert as HTML at cursor position
        document.execCommand('insertHTML', false, displayHtml);

        setTimeout(handleInput, 0);
    }, [readOnly, handleInput]);

    // ─── Toolbar Commands ──────────────────────────────────────────────────────

    const execCmd = (command: string, value: string | undefined = undefined) => {
        if (!supportsFormatting || readOnly) return;
        document.execCommand(command, false, value);
        handleInput();
        editorRef.current?.focus();
    };

    const handleCode = () => {
        if (!supportsFormatting || readOnly) return;
        const selection = window.getSelection();
        if (!selection) return;
        const text = selection.toString();
        document.execCommand('insertText', false, '`' + text + '`');
        handleInput();
    };

    const handleSpoiler = () => {
        if (!supportsFormatting || readOnly) return;
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return;

        const span = document.createElement('span');
        span.setAttribute('data-spoiler', 'true');
        span.className = 'spoiler-text';

        try {
            const range = selection.getRangeAt(0);
            range.surroundContents(span);
            handleInput();
        } catch {
            // Fallback for complex selections
            document.execCommand('insertText', false, '||' + selection.toString() + '||');
            handleInput();
        }
    };

    // ─── Link Modal ────────────────────────────────────────────────────────────

    const openLinkModal = () => {
        if (!supportsFormatting || readOnly) return;
        saveSelection();
        const selText = savedSelection.current ? savedSelection.current.toString() : '';
        setLinkText(selText);
        setLinkUrl('');
        setIsLinkModalOpen(true);
    };

    const applyLink = () => {
        if (!linkUrl || readOnly) {
            setIsLinkModalOpen(false);
            return;
        }
        editorRef.current?.focus();
        restoreSelection();

        const textToUse = linkText || linkUrl;
        // Sanitize URL — only allow http/https/tg protocols
        const cleanUrl = linkUrl.replace(/"/g, '%22');
        const isAllowedProtocol = /^https?:\/\/|^tg:\/\//.test(cleanUrl);
        if (!isAllowedProtocol) {
            setIsLinkModalOpen(false);
            return;
        }

        // Insert as markdown-parseable anchor in the editor HTML — Turndown will pick it up
        const html = `<a href="${cleanUrl}">${textToUse}</a>`;
        insertHtmlAtCursor(html);
        handleInput();

        setIsLinkModalOpen(false);
        setLinkUrl('');
        setLinkText('');
        savedSelection.current = null;
    };

    // ─── Emoji ─────────────────────────────────────────────────────────────────

    const handleEmojiSelect = (emoji: string) => {
        if (readOnly) return;
        editorRef.current?.focus();
        restoreSelection();
        insertHtmlAtCursor(emoji);
        handleInput();
        setShowEmoji(false);
        savedSelection.current = null;
    };

    // ─── Toolbar Component ─────────────────────────────────────────────────────

    const ToolbarButton = ({ icon: Icon, action, title, disabled = false, active = false }: any) => (
        <button
            onMouseDown={(e) => { 
                e.preventDefault();
                !disabled && action(); 
            }}
            disabled={disabled || readOnly}
            className={`p-1.5 rounded-md transition-all flex items-center justify-center ${
                (disabled || readOnly) 
                ? 'opacity-30 cursor-not-allowed' 
                : active 
                    ? 'bg-blue-100 text-blue-600'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-blue-600 active:bg-blue-50 active:scale-95'
            }`}
            title={title}
        >
            <Icon className="w-4 h-4" />
        </button>
    );

    // ─── Render ────────────────────────────────────────────────────────────────

    return (
        <div className={`flex flex-col h-full bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm ${!readOnly ? 'focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-500/10' : ''} transition-all relative`}>
            
            {/* STICKY TOOLBAR */}
            {!readOnly && (
                <div className="flex flex-wrap items-center gap-1 p-2 border-b border-slate-100 bg-white z-10 select-none sticky top-0">
                    {supportsFormatting ? (
                        <>
                            <div className="flex items-center gap-0.5 mr-2 pr-2 border-r border-slate-100">
                                <ToolbarButton icon={Undo} action={() => execCmd('undo')} title="Отменить" />
                                <ToolbarButton icon={Redo} action={() => execCmd('redo')} title="Повторить" />
                            </div>

                            <ToolbarButton icon={Bold} action={() => execCmd('bold')} title="Жирный" />
                            <ToolbarButton icon={Italic} action={() => execCmd('italic')} title="Курсив" />
                            <ToolbarButton icon={Strikethrough} action={() => execCmd('strikeThrough')} title="Зачеркнутый" />
                            
                            <div className="w-px h-5 bg-slate-200 mx-1" />
                            
                            <ToolbarButton icon={LinkIcon} action={openLinkModal} active={isLinkModalOpen} title="Вставить ссылку" />
                            <ToolbarButton icon={VenetianMask} action={handleSpoiler} title="Спойлер (Скрытый текст)" />
                            <ToolbarButton icon={Code} action={handleCode} title="Код" />
                        </>
                    ) : (
                        <div className="flex items-center px-2 text-xs font-medium text-slate-400 gap-2">
                            <Type className="w-4 h-4" />
                            <span>Форматирование недоступно для этой сети</span>
                        </div>
                    )}

                    <div className="flex-1" />

                    <div className="relative">
                        <button
                            onMouseDown={(e) => { 
                                e.preventDefault(); 
                                if (!showEmoji) saveSelection();
                                setShowEmoji(!showEmoji); 
                            }}
                            className={`p-1.5 rounded-md transition-all ${showEmoji ? 'bg-amber-100 text-amber-600' : 'text-slate-400 hover:bg-amber-50 hover:text-amber-500'}`}
                            title="Эмоджи"
                        >
                            <Smile className="w-5 h-5" />
                        </button>
                        {showEmoji && (
                            <EmojiPicker onSelect={handleEmojiSelect} onClose={() => setShowEmoji(false)} />
                        )}
                    </div>
                </div>
            )}

            {/* SCROLLABLE EDITOR AREA */}
            <div className="flex-1 relative bg-white cursor-text overflow-hidden">
                <div
                    ref={editorRef}
                    contentEditable={!readOnly}
                    suppressContentEditableWarning
                    onFocus={() => { isFocused.current = true; }}
                    onBlur={() => { isFocused.current = false; }}
                    onInput={handleInput}
                    onPaste={handlePaste}
                    onKeyDown={handleKeyDown}
                    onCompositionStart={() => { isComposing.current = true; }}
                    onCompositionEnd={() => { isComposing.current = false; handleInput(); }}
                    className={`w-full h-full p-4 outline-none text-slate-800 text-sm leading-relaxed overflow-y-auto custom-scrollbar prose prose-sm max-w-none ${readOnly ? 'bg-slate-50' : ''}`}
                    style={{ 
                        minHeight: '150px',
                        wordBreak: 'break-word',
                    }}
                />
                
                {/* Custom Placeholder */}
                {!readOnly && (!value || value.trim() === '') && (
                    <div 
                        className="absolute top-4 left-4 text-slate-400 pointer-events-none select-none text-sm"
                    >
                        {placeholder}
                    </div>
                )}
            </div>

            {/* LINK MODAL POPUP */}
            {!readOnly && isLinkModalOpen && (
                <div className="absolute top-12 left-2 z-50 bg-white p-3 rounded-xl shadow-xl border border-slate-200 w-72 animate-in fade-in zoom-in-95 ring-4 ring-slate-100">
                    <div className="flex justify-between items-center mb-2">
                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wide">Вставить ссылку</h4>
                        <button onClick={() => setIsLinkModalOpen(false)} className="text-slate-300 hover:text-slate-500">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="space-y-2">
                        <div>
                            <input 
                                type="text" 
                                value={linkText}
                                onChange={e => setLinkText(e.target.value)}
                                placeholder="Текст ссылки"
                                className="w-full text-sm px-3 py-2 bg-white text-slate-800 border border-slate-200 rounded-lg outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all placeholder:text-slate-400"
                            />
                        </div>
                        <div>
                            <input 
                                type="text" 
                                value={linkUrl}
                                onChange={e => setLinkUrl(e.target.value)}
                                placeholder="https://example.com"
                                autoFocus
                                onKeyDown={(e) => e.key === 'Enter' && applyLink()}
                                className="w-full text-sm px-3 py-2 bg-white text-slate-800 border border-slate-200 rounded-lg outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all font-mono text-xs placeholder:text-slate-400"
                            />
                        </div>
                        <button 
                            onClick={applyLink} 
                            disabled={!linkUrl}
                            className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Check className="w-4 h-4 mr-1.5" />
                            Применить
                        </button>
                    </div>
                </div>
            )}
            
            {/* Visual Styles for ContentEditable */}
            <style>{`
                /* Typography & Spacing Fixes */
                [contenteditable] p { margin-top: 0; margin-bottom: 0.25em; }
                [contenteditable] br + br { display: block; content: ''; margin-top: 0.25em; }
                
                [contenteditable] pre { 
                    background-color: #f1f5f9; 
                    padding: 0.5rem; 
                    border-radius: 0.375rem; 
                    font-family: monospace; 
                    margin: 0.5em 0;
                    border: 1px solid #e2e8f0;
                }
                [contenteditable] a { color: #2563eb; text-decoration: underline; cursor: pointer; }
                [contenteditable] ul { list-style: disc; padding-left: 1.5em; margin: 0.25em 0; }
                [contenteditable] ol { list-style: decimal; padding-left: 1.5em; margin: 0.25em 0; }
                
                /* Spoiler Style */
                .spoiler-text, [data-spoiler] {
                    background-color: #334155;
                    color: transparent;
                    border-radius: 4px;
                    padding: 0 2px;
                    cursor: help;
                    transition: all 0.2s;
                    text-shadow: none;
                    user-select: all;
                }
                .spoiler-text:hover, [data-spoiler]:hover {
                    background-color: #e2e8f0;
                    color: #334155;
                }
            `}</style>
        </div>
    );
};
